import { BehaviorSubject, Observable, Subject, combineLatest, Subscription } from 'rxjs';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {
  EvchargeTelemetryState,
  Inverter,
  isNotNullOrUndefined,
  PlantDeviceType,
  ProductModelService,
  System,
} from 'ngx-core-services';
import { map, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import {
  SystemDefinition,
  SystemsState,
  requestSystemsDevicesStatus,
  selectedSystem,
  selectedSystemConnextInfoSn,
  selectedSystemMonitorPref,
  selectedSystemUid,
  selectedSystemVerifiedDevices,
  selectedSystemVerifiedUninstallations,
} from '../../store';
import { selectSystemDeviceStatusBySelectedSystem } from '../../store/selectors/device-list.selectors';

const UPDATED_TELEMETRY_PERIOD_THRESHOLD = 30;
const MAX_DELAY_TELEMETRY_PERIOD_THRESHOLD = 30;

const CHECK_TELEMETRY_STATUS = ['inverter', 'meter', 'enerclic', 'connext', 'azzurro-hub'];

const labels = {
  actions: 'Actions',
  logger: 'Logger',
  noActions: 'No actions',
  noDevice: 'No device found',
  compareLimitLabel: 'Select inverters to compare',
  compareCheckboxAriaLabel: 'Select for comparison',
  selectedDevices: 'Selected',
  serial: 'Serial',
  status: 'Status',
  statusConnected: 'Connected',
  statusDisconnected: 'Disconnected',
  statusNotUpdated: 'Not updated',
  statusUpdated: 'Updated',
  statusUninstalled: 'Uninstalled',
  title: 'Device list',
  type: 'Type',
  evChIdle: 'Idle',
  evChCharging: 'Charging',
  evChError: 'Error',
  evChUnknown: 'Unknown',
  evChParking: 'Parking',
};

@CreateBlockReduxSupport()
@Component({
  selector: 'lib-device-list',
  templateUrl: './device-list.component.html',
  styleUrls: ['./device-list.component.scss'],
})
export class DeviceListComponent implements IReduxBlock, OnDestroy {
  @Output() view = new EventEmitter<string>();
  @Output() compare = new EventEmitter<string>();
  @Output() inverterCountChange = new EventEmitter<number>();

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() compareMode = false;
  @Input() selectedDevicesForComparison: string[] = [];

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  displayedColumns: string[] = ['serial', 'status', 'type', 'logger', 'actions'];

  dataSource = new MatTableDataSource<any>([]);

  readonly isVirtualSystem$ = this.store.select(selectedSystemMonitorPref).pipe(
    map((pref) => pref === 'Solarman')
  );

  readonly requestSystemDevicesStatus$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, forceRefresh, instanceId]) =>
      this.store.dispatch(requestSystemsDevicesStatus({ systemId, instanceId, forceRefresh }))
    )
  );

  readonly devices$ = combineLatest([
    this._instanceId$.pipe(
      switchMap((instanceId) =>
        this.store
          .select(selectSystemDeviceStatusBySelectedSystem(instanceId))
          .pipe(isNotNullOrUndefined())
      )
    ),
    this.store.select(selectedSystemVerifiedDevices),
    this.store.select(selectedSystemVerifiedUninstallations),
    this.store.select(selectedSystemMonitorPref).pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystem).pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystemConnextInfoSn),
  ]).pipe(
    map(
      ([
        devices,
        verifiedInstallations,
        verifiedUninstallations,
        monitorPref,
        system,
        connextSn,
      ]) =>
        this.adaptDeviceForTable(
          devices,
          verifiedInstallations ?? [],
          verifiedUninstallations ?? [],
          monitorPref,
          system,
          connextSn
        )
    ),
    tap((devicesArray) => {
      this.dataSource.data = devicesArray;

      // Count inverters (excluding uninstalled ones)
      const inverterCount = devicesArray.filter(
        (device) => device.type === 'inverter' && !device.uninstalled
      ).length;
      this.inverterCountChange.emit(inverterCount);

      this.changeDetectorRef.detectChanges();
    })
  );

  // Subscribe to devices$ to ensure dataSource updates
  private devicesSubscription?: Subscription;

  constructor(
    readonly store: Store<SystemsState>,
    readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.convertTelemetryStatusToLabel = this.convertTelemetryStatusToLabel.bind(this);
    this.isDeviceSelectedForComparison = this.isDeviceSelectedForComparison.bind(this);
    // Initialize subscription after all observables are defined
    this.devicesSubscription = this.devices$.subscribe();
  }

  isDeviceSelectedForComparison(deviceSerial: string): boolean {
    return this.selectedDevicesForComparison.includes(deviceSerial);
  }

  ngOnDestroy() {
    this.devicesSubscription?.unsubscribe();
  }

  adaptDeviceForTable(
    devices: { [key: string]: Partial<Inverter> },
    installations: PlantDeviceType[],
    uninstallations: PlantDeviceType[],
    monitorPref: string,
    system: SystemDefinition,
    connextSn: string | undefined | null
  ) {
    const devicesArray = Object.entries(devices)
      .filter(([serial]) => {
        const productType = new ProductModelService(serial).type?.name || '';
        return CHECK_TELEMETRY_STATUS.includes(productType);
      })
      .map(([serial, packet]) => {
        const product = new ProductModelService(serial);
        const productType = product.type?.name || 'Unknown';

        const telemetryState = this.getDeviceStatus(packet.TS);
        const gwId = packet.GwId || '---';

        const serialDisplay =
          serial.toLocaleUpperCase() === gwId.toLocaleUpperCase()
            ? connextSn || system.connextSn || serial
            : serial;

        return {
          serial: serialDisplay.toUpperCase(),
          telemetryState,
          evcState: null as EvchargeTelemetryState | null | undefined,
          type: productType,
          logger: gwId,
          uninstalled: false,
          telemetryStateCssClass: this.getTelemetryStateCssClass(telemetryState),
        };
      });


    uninstallations.forEach((device) => {
      const productType = new ProductModelService(device.sn).type?.name || '';

      devicesArray.push({
        serial: device.sn.toUpperCase(),
        telemetryState: undefined,
        evcState: null,
        telemetryStateCssClass: 'chip-uninstalled',
        type: productType,
        logger: '---',
        uninstalled: true,
      });
    });

    return devicesArray;
  }

  getDeviceStatus(deviceTs: number | undefined): boolean | null | undefined {
    if (!deviceTs) {
      return null;
    }

    const now = Date.now();
    const telemetryTime = deviceTs * 1000;
    const updatedThresholdMs = UPDATED_TELEMETRY_PERIOD_THRESHOLD * 60 * 1000;
    const maxDelayAcceptedMs = MAX_DELAY_TELEMETRY_PERIOD_THRESHOLD * 60 * 1000;

    if (now - telemetryTime <= updatedThresholdMs) {
      return true; // Connected
    } else if (now - telemetryTime <= maxDelayAcceptedMs) {
      return null; // Disconnected
    } else {
      return false; // Not updated
    }
  }

  getTelemetryStateCssClass(telemetryState: boolean | null | undefined): string {
    if (telemetryState === true) {
      return 'chip-updated';
    } else {
      return 'chip-not-updated';
    }
  }

  convertTelemetryStatusToLabel(status: boolean | null | undefined): string {
    switch (status) {
      case true:
        return this.labels.statusUpdated;
      default:
        return this.labels.statusNotUpdated;
    }
  }
}
